import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { REACT_APP_API_URL, ROUTE } from 'src/constants';

import { adminCurrenciesSelector, getAdminCurrencies } from 'src/store/adminCurrency';
import {
  adminExchangesMetaSelector,
  adminExchangesSelector,
  getAdminExchanges,
} from 'src/store/adminExchanges';
import {
  exchangesMerchantSelector,
  exchangesParsersSelector,
  getExchangesGivenMerchant,
  getExchangesParsers,
  // getStatuses,
  // statusesSelector,
} from 'src/store/exchanges';

export const useBuild = () => {
  const { t } = useTranslation('adminBuilder');

  const dispatch = useDispatch();

  const exchanges = useSelector(adminExchangesSelector);
  const exchangesMeta = useSelector(adminExchangesMetaSelector);
  const currencies = useSelector(adminCurrenciesSelector);
  const merchants = useSelector(exchangesMerchantSelector);
  const parsers = useSelector(exchangesParsersSelector);
  // const statuses = useSelector(statusesSelector);

  const [searchParams, setSearchParams] = useSearchParams();

  const [filtersData, setFiltersData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(getAdminCurrencies());
    dispatch(getExchangesGivenMerchant());
    dispatch(getExchangesParsers());
  }, [dispatch]);

  useEffect(() => {
    const convertedParams = Object.fromEntries(searchParams.entries());

    setFiltersData(convertedParams);
    setCurrentPage(Number(convertedParams?.page) || 1);

    dispatch(getAdminExchanges(convertedParams));
  }, [dispatch, searchParams]);

  const onSubmitFilters = useCallback(
    (filters) => {
      setCurrentPage(1);

      dispatch(getAdminExchanges({ ...filters }));
    },
    [dispatch],
  );

  const onChangePage = useCallback(
    (page) => {
      setCurrentPage(page);

      const convertedParams = Object.fromEntries(searchParams.entries());

      const data = { ...convertedParams, ...filtersData, page: page?.toString() };

      for (const key in data) {
        if (!data[key]) {
          delete data[key];
        }
      }

      setSearchParams(data);
      dispatch(getAdminExchanges(data));
    },
    [searchParams, filtersData, setSearchParams, dispatch],
  );

  const updatedCurrencies =
    currencies?.map((item) => ({ value: item?.id?.toString(), label: item?.name })) || [];

  const updatedMerchants =
    merchants?.map((item) => ({ value: item, label: t(`merchant:${item}`) })) || [];

  const updatedParsers = parsers?.map((item) => ({ value: item, label: item })) || [];
  // const updatedStatuses =
  // statuses?.map((item) => ({ value: item, label: t(`statuses:${item}`) })) || [];

  const build = {
    title: t('exchangeDirection:main'),
    table_data: exchanges,
    table_meta: { ...exchangesMeta, current_page: currentPage, on_change_page: onChangePage },
    filters_data: filtersData,
    set_filters_data: setFiltersData,
    on_submit_filters: onSubmitFilters,
    main_route: ROUTE.ADMIN.EXCHANGE_DIRECTION.MAIN,
    table_columns: [
      {
        id: 5,
        key: 'id',
        label: t('label:id'),
      },
      {
        id: 1,
        key: 'givingCurrency',
        label: t('label:givingCurrency'),
        value: ({ given }) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={REACT_APP_API_URL + given?.image}
              alt={REACT_APP_API_URL + given?.image}
              style={{ width: 18, height: 18, marginRight: 10 }}
            />
            {given?.code}
          </div>
        ),
      },
      {
        id: 2,
        key: 'receivingCurrency',
        label: t('label:receivingCurrency'),
        value: ({ received }) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={REACT_APP_API_URL + received?.image}
              alt={REACT_APP_API_URL + received?.image}
              style={{ width: 18, height: 18, marginRight: 10 }}
            />
            {received?.code}
          </div>
        ),
      },
      {
        id: 3,
        key: 'exchangeCourse',
        label: t('label:exchangeCourse'),
        value: ({ given, received }) => {
          return (
            <div>
              {given.rate_given_currency}{' '}
              <span className="admin-builder__text--gray">{given.code}</span> ={' '}
              {received.rate_received_currency}{' '}
              <span className="admin-builder__text--gray">{received.code}</span>
            </div>
          );
        },
      },
      {
        id: 4,
        key: 'minMaxSumm',
        label: t('label:minMaxSumm'),
        value: ({ given, received, min_exchange, max_exchange }) => {
          return (
            <div>
              {min_exchange} <span className="admin-builder__text--gray">{given.code}</span> /{' '}
              {max_exchange} <span className="admin-builder__text--gray">{received.code}</span>
            </div>
          );
        },
      },
    ],
    filters: [
      {
        key: 'id',
        label: t('searchById'),
        type: 'input',
      },
      {
        key: 'currency_given_id',
        label: t('givingCurrency'),
        type: 'select',
        options: updatedCurrencies,
      },
      {
        key: 'currency_received_id',
        label: t('receivingCurrency'),
        type: 'select',
        options: updatedCurrencies,
      },
      {
        key: 'given_merchant',
        label: t('incomeMerchant'),
        type: 'select',
        options: updatedMerchants,
      },
      {
        key: 'received_merchant',
        label: t('outcomeMerchant'),
        type: 'select',
        options: updatedMerchants,
      },
      {
        key: 'parser',
        label: t('parser'),
        type: 'select',
        options: updatedParsers,
      },
      // {
      //   key: 'status',
      //   label: t('status'),
      //   type: 'select',
      //   options: updatedStatuses,
      // },
      {
        key: 'outcomeMerchant',
        label: t('createdDate'),
        type: 'datepicker',
      },
    ],
  };

  return { build };
};
